import React from 'react';

interface PaginationButtonsProps {
  totalPages: number;
  currentPage: number;
  handlePageChange: (page: number | string) => void;
}

const PaginationButtons = ({ totalPages, currentPage, handlePageChange }: PaginationButtonsProps) => {
  const goTo = (e: React.MouseEvent, page: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      handlePageChange(page);
    }
  };

  const renderPageButton = (page: number) => (
    <li key={page}>
      <button
        type="button"
        onClick={(e) => goTo(e, page)}
        className={`flex items-center justify-center px-3 h-8 leading-tight text-white border border-gray-300 ${
          page === currentPage
            ? 'bg-storyblueSecondLightText hover:bg-storyblueSecondLightText dark:bg-storygreen dark:hover:bg-storygreen'
            : 'bg-storyblue hover:bg-storyblueSecondLightText dark:bg-storyblue dark:hover:bg-storygreen'
        }`}
      >
        <span className={page === currentPage ? 'text-white font-bold' : ''}>{page}</span>
      </button>
    </li>
  );

  const renderEllipsis = (key: string) => (
    <li key={key}>
      <span className="p-1 font-bold ">. . .</span>
    </li>
  );

  const renderPaginationButtons = () => {
    const pages = Array.from({ length: totalPages }, (_, index) => index + 1);
    if (totalPages <= 3) {
      return pages.map(renderPageButton);
    }

    const firstPage = 1;
    const lastPage = totalPages;
    const currentPageIndex = currentPage - 1;
    const buttons: (number | string)[] = [];
    if (currentPageIndex < 2) {
      buttons.push(...pages.slice(0, 3), '...', lastPage);
    } else if (currentPageIndex >= totalPages - 2) {
      buttons.push(firstPage, '...', ...pages.slice(-3));
    } else {
      buttons.push(
        firstPage,
        '...',
        ...pages.slice(currentPageIndex - 1, currentPageIndex + 2),
        '...',
        lastPage,
      );
    }
    return buttons.map((b, i) =>
      b === '...' ? renderEllipsis(`ellipsis-${i}`) : renderPageButton(b as number),
    );
  };

  return (
    <nav aria-label="Page navigation example">
      <ul className="flex items-center -space-x-px h-8 text-sm">
        <li>
          <button
            type="button"
            onClick={(e) => goTo(e, currentPage - 1)}
            disabled={currentPage <= 1}
            className="flex items-center justify-center px-3 h-8 ms-0 leading-tight text-white bg-storyblue border border-e-0 border-gray-300 rounded-s-lg hover:bg-storyblueSecondLightText dark:bg-storyblue dark:text-white dark:hover:bg-storyblueSecondLightText dark:hover:text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="sr-only">Previous</span>
            <svg
              className="w-2.5 h-2.5 rtl:rotate-180"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 6 10"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 1 1 5l4 4"
              />
            </svg>
          </button>
        </li>
        {renderPaginationButtons()}
        <li>
          <button
            type="button"
            onClick={(e) => goTo(e, currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="flex items-center justify-center px-3 h-8 leading-tight text-white bg-storyblue border border-e-0 border-gray-300 rounded-e-lg hover:bg-storygreen dark:bg-storyblue dark:text-white dark:hover:bg-storygreen dark:hover:text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="sr-only">Next</span>
            <svg
              className="w-2.5 h-2.5 rtl:rotate-180"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 6 10"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 9 4-4-4-4"
              />
            </svg>
          </button>
        </li>
      </ul>
    </nav>
  );
};
export default PaginationButtons;
